import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { I18nProvider } from './i18n/context';
import { applyStoredUiLang } from './lib/uiLocale';
import { patchFetchForOffline } from './offline/offlineFetch';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// ДОПОЛНИТЕЛЬНАЯ БЛОКИРОВКА Web3/MetaMask (основная блокировка в index.html)
// Это дополнительный слой защиты на случай, если расширения попытаются установить объекты после загрузки
if (typeof window !== 'undefined') {
  // Список объектов Web3, которые нужно заблокировать
  const web3BlockedObjects = ['ethereum', 'web3', 'Web3', 'solana', 'phantom', 'keplr', 'petra', 'suiet'];

  // Создаем простую заглушку
  const createBlockedStub = () => ({
    request: () => Promise.reject(new Error('Web3 disabled by application policy.')),
    send: () => Promise.reject(new Error('Web3 disabled by application policy.')),
    sendAsync: () => Promise.reject(new Error('Web3 disabled by application policy.')),
    enable: () => Promise.reject(new Error('Web3 disabled by application policy.')),
    connect: () => Promise.reject(new Error('Web3 disabled by application policy.')),
    disconnect: () => Promise.reject(new Error('Web3 disabled by application policy.')),
    isConnected: false,
    isMetaMask: false,
    on: () => {},
    removeListener: () => {},
    removeAllListeners: () => {}
  });

  // Применяем блокировку для каждого объекта
  web3BlockedObjects.forEach(objName => {
    try {
      // Проверяем, существует ли уже свойство
      const descriptor = Object.getOwnPropertyDescriptor(window, objName);

      // Если свойство не существует или его можно переопределить
      if (!descriptor || descriptor.configurable !== false) {
        try {
          Object.defineProperty(window, objName, {
            value: createBlockedStub(),
            writable: false,
            configurable: false,
            enumerable: false
          });
        } catch (e) {
          // Если defineProperty не сработал, просто присваиваем
          try {
            window[objName] = createBlockedStub();
          } catch (e2) {
            // Игнорируем все ошибки
          }
        }
      }
    } catch (e) {
      // Игнорируем все ошибки блокировки
    }
  });
}

// Подавление всех ошибок от расширений браузера
if (typeof window !== 'undefined') {
  // Функция для определения ошибок от расширений
  // Согласно ТЗ: полный список паттернов для фильтрации Web3-расширений
  const isExtensionError = (error) => {
    if (!error) return false;
    const errorMessage = error?.message || error?.toString() || '';
    const errorStack = error?.stack || '';
    const errorString = String(error);
    
    // Объединяем все строки для проверки
    const combinedText = `${errorMessage} ${errorStack} ${errorString}`.toLowerCase();
    
    const extensionPatterns = [
      // Основные Web3-кошельки
      /metamask/i,
      /phantom/i,
      /solana/i,
      /ethereum/i,
      /web3/i,
      /wallet/i,
      
      // Расширенные названия кошельков (согласно ТЗ)
      /keplr/i,
      /petra/i,
      /suiet/i,
      /aptos/i,
      /sui/i,
      /station/i,
      /slush/i,
      
      // Расширения браузера
      /chrome-extension:/i,
      /moz-extension:/i,
      /safari-extension:/i,
      /extension:/i,
      
      // Общие паттерны расширений
      /content.*script/i,
      /background.*script/i,
      /injected.*script/i,
      /page.*script/i,
      
      // Специфичные ошибки расширений
      /establish.*connection/i,
      /receiving.*end.*not.*exist/i,
      /message.*port.*closed/i,
      /extension.*context/i,
      /cross.*origin/i,
      
      // Дополнительные паттерны
      /wallet.*connect/i,
      /walletlink/i,
      /coinbase.*wallet/i,
      /trust.*wallet/i,
      /ledger/i,
      /trezor/i
    ];
    
    return extensionPatterns.some(pattern => pattern.test(combinedText));
  };

  // Перехватываем все ошибки
  const originalErrorHandler = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (isExtensionError(error || { message })) {
      // Подавляем ошибки от расширений
      return true;
    }
    // Для остальных ошибок вызываем оригинальный обработчик
    if (originalErrorHandler) {
      return originalErrorHandler.call(this, message, source, lineno, colno, error);
    }
    return false;
  };

  // Перехватываем unhandledrejection
  window.addEventListener('unhandledrejection', function(event) {
    if (isExtensionError(event.reason)) {
      event.preventDefault();
      return;
    }
  });

  // Перехватываем console.error для фильтрации
  const originalConsoleError = console.error;
  console.error = function(...args) {
    const errorText = args.join(' ');
    if (isExtensionError({ message: errorText })) {
      // Подавляем ошибки от расширений в консоли
      return;
    }
    originalConsoleError.apply(console, args);
  };
}

applyStoredUiLang();

// Offline-first fetch caching + queueing for /api/*
patchFetchForOffline();
serviceWorkerRegistration.unregister();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <I18nProvider>
      <App />
    </I18nProvider>
  </React.StrictMode>
);

